import { c as _c } from "react/compiler-runtime";
import { RechartsScatter as Scatter, useRechartsActiveTooltipDataPoints as useActiveTooltipDataPoints, type RechartsScatterProps as ScatterProps, type RechartsScatterShapeProps as ScatterShapeProps } from '@precis-digital/kurama/recharts';
import { useTheme } from '@precis-digital/kurama/utils';
import type { ReactElement } from 'react';
import { useChartCardDefaults } from '../../hooks/useChartCardDefaults';
import { OTHERS_NON_ACTIVE_FILL_OPACITY } from '../../constants';
import type { CustomScatterDrawingProps } from '../../types';
type RealScatterShapeProps = ScatterShapeProps & Pick<ScatterProps, 'fill' | 'fillOpacity' | 'stroke' | 'strokeWidth' | 'strokeOpacity'> & {
  isActive?: boolean;
};
const renderShape = (props: RealScatterShapeProps, badgeRx: number): ReactElement | null => {
  const {
    cx,
    cy,
    fill,
    fillOpacity,
    stroke,
    strokeWidth,
    strokeOpacity
  } = props;
  if (cx == null || cy == null) return null;
  const size = 12;
  const x = cx - size / 2;
  const y = cy - size / 2;
  return <rect x={x} y={y} width={size} height={size} rx={badgeRx} ry={badgeRx} fill={fill} fillOpacity={fillOpacity} stroke={stroke} strokeWidth={strokeWidth} strokeOpacity={strokeOpacity} />;
};
const getShapeProps = (shapeProps: RealScatterShapeProps, colorMap: Record<string, string> | undefined, dataKey: string): RealScatterShapeProps => {
  const {
    fill,
    payload
  } = shapeProps;
  const value = payload?.[dataKey];
  const mappedColor = value == null ? fill : colorMap?.[value] ?? fill;
  return {
    ...shapeProps,
    fill: mappedColor
  };
};
const CustomScatter = t0 => {
  const $ = _c(22);
  let children;
  let colorMap;
  let dataKey;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      colorMap,
      dataKey,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = colorMap;
    $[3] = dataKey;
    $[4] = props;
  } else {
    children = $[1];
    colorMap = $[2];
    dataKey = $[3];
    props = $[4];
  }
  const theme = useTheme();
  const defaults = useChartCardDefaults();
  const activeDataRow = useActiveTooltipDataPoints();
  let t1;
  if ($[5] !== theme.borderRadius.xxSmall) {
    t1 = Number.parseFloat(theme.borderRadius.xxSmall.replace("px", ""));
    $[5] = theme.borderRadius.xxSmall;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const badgeRx = t1;
  let t2;
  if ($[7] !== activeDataRow || $[8] !== badgeRx || $[9] !== colorMap || $[10] !== dataKey || $[11] !== defaults) {
    t2 = props_0 => {
      const shapeProps = getShapeProps(props_0, colorMap, dataKey);
      if (activeDataRow == null) {
        return renderShape(shapeProps, badgeRx);
      } else {
        return renderShape({
          ...shapeProps,
          fillOpacity: defaults.others.nonActiveFillOpacity ?? OTHERS_NON_ACTIVE_FILL_OPACITY
        }, badgeRx);
      }
    };
    $[7] = activeDataRow;
    $[8] = badgeRx;
    $[9] = colorMap;
    $[10] = dataKey;
    $[11] = defaults;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  const shape = t2;
  let t3;
  if ($[13] !== badgeRx || $[14] !== colorMap || $[15] !== dataKey) {
    t3 = props_1 => renderShape(getShapeProps(props_1, colorMap, dataKey), badgeRx);
    $[13] = badgeRx;
    $[14] = colorMap;
    $[15] = dataKey;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  const activeShape = t3;
  let t4;
  if ($[17] !== activeShape || $[18] !== children || $[19] !== props || $[20] !== shape) {
    t4 = <Scatter {...props} shape={shape} activeShape={activeShape}>{children}</Scatter>;
    $[17] = activeShape;
    $[18] = children;
    $[19] = props;
    $[20] = shape;
    $[21] = t4;
  } else {
    t4 = $[21];
  }
  return t4;
};
export default CustomScatter;